import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { corPessoa, faixaPessoa, linkDoPortal, primeiroNome, rotinaDe, TELA_PEND_TRANSP } from './rotina';

// Tela de cada atividade, para o link do nome (as que não têm tela própria ficam sem).
const PAGINA_ATIV = {
  'Disp. p/ Retirada': '/retirada', 'Pag. Não Aprovado': '/pagamento', 'Cancelamentos (AES)': '/cancelamentos',
  'Falta na Produção (ETR)': '/etr', 'Erro na Nota': '/erro-nota', 'Pendência de Transportadora': TELA_PEND_TRANSP,
  'Importar dados': '/importar',
};
// Atividades que aparecem todo dia, mesmo com o número zerado: a Pendência de Transportadora
// (até 28/10 o link abria a planilha do SharePoint) e o Importar dados (as 3 bases do dia,
// Adnéia, 29/10/2026).
const SEMPRE_ATIV = new Set(['Pendência de Transportadora', 'Importar dados']);

const NIVEL = {
  verde: { cor: 'bg-emerald-500', txt: 'em dia' },
  amarelo: { cor: 'bg-amber-400', txt: 'atenção' },
  vermelho: { cor: 'bg-red-500', txt: 'fora da meta' },
};

// Missões do dia (Adnéia, 17/10/2026): por pessoa, o que ela aciona, encerra e confere no
// dia, tirado das listas do rodízio e da rotina de cada status. Bolinha verde: feito hoje
// (o ✓ da verificação); vermelha: pendente. Desde 17/10 à noite mora na janela do botão
// "Minhas demandas" / "Rodízio" (embutido), e não mais no Dashboard: cada atendente vê só
// o seu cartão (somente); a gestão vê os três.
//   verifRows: linhas da Verificação de status (motivo, qtd, dias_acionar, ultima, donos)
//   ativRows:  [{ motivo, qtd, ultima }] da Verificação de atividades
//   donoDe(item): quem está com o item no dia de referência (null = ninguém)
//   pessoas: nomes das listas do dia; gestao: nome da gestão; dataRef: {data, data_br, ehHoje}
//   portais: { canal: link } da aba Acessos; metas: as metas da Visão geral (semáforo)
//   somente: nome de quem está vendo (mostra só o cartão dela); embutido: sem moldura
const MissoesHoje = ({ verifRows, ativRows, donoDe, pessoas, gestao, dataRef, portais = {}, metas = [],
  somente = null, embutido = false }) => {
  const { user } = useAuth();
  const chave = `elo_missoes_aberto_${user?.email || 'x'}`;
  const [aberto, setAberto] = useState(() => {
    try { return localStorage.getItem(chave) !== '0'; } catch { return true; }
  });
  const alternar = () => setAberto(v => {
    try { localStorage.setItem(chave, v ? '0' : '1'); } catch { /* sem localStorage */ }
    return !v;
  });

  if (!dataRef || !pessoas.length) return null;
  const hojeStr = dataRef.data;
  const diaSemana = new Date(dataRef.data + 'T12:00:00').getDay();
  const ehHoje = dataRef.ehHoje;

  const todos = [...pessoas, gestao].filter((p, i, a) => p && a.indexOf(p) === i);
  const porPessoa = Object.fromEntries(todos.map(p => [p, { acionar: [], encerrar: [], conferir: [], ativ: [] }]));

  verifRows.forEach(r => {
    if (!r.qtd) return;
    const rot = rotinaDe(r.motivo);
    if (rot.encerrar) {
      // Entregue: quem deixou assim encerra (decisão 8). Nome fora das listas vai à gestão.
      (r.donos || []).forEach(d => {
        const alvo = todos.find(p => primeiroNome(p) === primeiroNome(d.atendente)) || gestao;
        if (alvo && porPessoa[alvo]) porPessoa[alvo].encerrar.push({ motivo: r.motivo, qtd: d.qtd, atendente: d.atendente, texto: rot.acao, feito: false });
      });
      return;
    }
    const dono = donoDe(r.motivo);
    if (!dono || !porPessoa[dono]) return;
    const item = { motivo: r.motivo, qtd: r.qtd, feito: r.ultima === hojeStr, portal: rot.portal };
    if ((r.dias_acionar || []).includes(diaSemana)) porPessoa[dono].acionar.push({ ...item, texto: rot.acao });
    else porPessoa[dono].conferir.push({ ...item, texto: rot.conf });
  });
  ativRows.forEach(a => {
    const dono = donoDe(a.motivo);
    if (!dono || !porPessoa[dono]) return;
    if (!a.qtd && !SEMPRE_ATIV.has(a.motivo)) return;
    porPessoa[dono].ativ.push({ motivo: a.motivo, qtd: a.qtd, feito: a.ultima === hojeStr });
  });

  const visiveis = somente ? todos.filter(p => primeiroNome(p) === primeiroNome(somente)) : todos;

  const pendentes = (p) => {
    const m = porPessoa[p];
    return [...m.acionar, ...m.encerrar, ...m.conferir, ...m.ativ].filter(it => !it.feito).length;
  };

  const linha = (p, tipo, it, cor) => {
    const href = it.portal && linkDoPortal(it.portal, portais);
    const abrirTela = tipo === 'ativ' && SEMPRE_ATIV.has(it.motivo);
    return (
      <li key={`${p}|${tipo}|${it.motivo}|${it.atendente || ''}`} className="flex items-start gap-2 text-sm">
        <span className={`mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full ${it.feito ? 'bg-emerald-500' : 'bg-red-500'}`}
          title={it.feito ? 'Feito hoje' : 'Pendente'} aria-label={it.feito ? 'feito hoje' : 'pendente'} />
        <span className={`w-9 flex-shrink-0 text-right font-bold tabular-nums ${cor}`}>{it.qtd}</span>
        <span className="min-w-0">
          {tipo === 'ativ' && PAGINA_ATIV[it.motivo]
            ? <Link to={PAGINA_ATIV[it.motivo]} className="hover:underline">{it.motivo}</Link>
            : tipo === 'conferir' ? it.motivo : (it.texto || it.motivo)}
          {abrirTela && (
            <Link to={PAGINA_ATIV[it.motivo]}
              className="ml-1.5 text-xs font-semibold text-blue-700 dark:text-blue-300 underline">Abrir tela</Link>
          )}
          {/* No "Só conferir" o status vem primeiro; o texto só aparece quando diz algo
              além de "conferir" (ex.: "Confirmar se o galpão barrou"). */}
          {tipo === 'conferir' && it.texto && !['Conferir', 'Dar uma olhada'].includes(it.texto) &&
            <span className="text-xs opacity-70"> · {it.texto.toLowerCase()}</span>}
          {tipo === 'acionar' && <span className="text-xs opacity-70"> · {it.motivo}</span>}
          {tipo === 'encerrar' && <span className="text-xs opacity-70"> · {it.motivo}, deixados por {it.atendente}</span>}
          {href && (
            <a href={href} target="_blank" rel="noopener noreferrer"
              className="ml-1.5 text-xs font-semibold text-blue-700 dark:text-blue-300 underline">Abrir {it.portal}</a>
          )}
        </span>
      </li>
    );
  };

  const grupo = (p, tipo, titulo, itens, cor) => itens.length > 0 && (
    <div className="space-y-1">
      <div className="text-[11px] font-bold uppercase tracking-wider opacity-70">{titulo}</div>
      <ul className="space-y-1">{itens.map(it => linha(p, tipo, it, cor))}</ul>
    </div>
  );

  const cartoes = (
    <div className={`grid gap-3 ${visiveis.length > 1 ? 'md:grid-cols-2 xl:grid-cols-3' : ''}`}>
      {visiveis.length === 0 && <p className="text-sm text-muted-foreground">Você não está nas listas do rodízio neste dia.</p>}
      {visiveis.map(p => {
        const m = porPessoa[p];
        const vazio = !m.acionar.length && !m.encerrar.length && !m.conferir.length && !m.ativ.length;
        return (
          <div key={p} className={`rounded-lg border p-3 space-y-3 min-w-0 ${faixaPessoa(p)}`}>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-bold px-2 py-0.5 rounded ${corPessoa(p)}`}>{p}</span>
              {p === gestao && !pessoas.includes(p) && <span className="text-xs opacity-70">gestão</span>}
              <span className="ml-auto text-xs opacity-70">{pendentes(p)} pendente{pendentes(p) === 1 ? '' : 's'}</span>
            </div>
            {p === gestao && metas.length > 0 && (
              <div className="space-y-1.5">
                <div className="text-[11px] font-bold uppercase tracking-wider opacity-70">Metas</div>
                {metas.map(mt => {
                  const nv = NIVEL[mt.nivel] || (mt.ok ? NIVEL.verde : NIVEL.vermelho);
                  return (
                    <div key={mt.chave} className="flex items-start gap-2 text-[13px]">
                      <span className={`mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full ${nv.cor}`} title={nv.txt} aria-label={nv.txt} />
                      <span className="min-w-0 flex-1">
                        <span className="font-semibold">{mt.rotulo}</span>
                        {mt.falta && <span className="block opacity-75">{mt.falta}</span>}
                      </span>
                      <span className="font-bold tabular-nums whitespace-nowrap">{mt.valor}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {grupo(p, 'acionar', 'Acionar hoje', m.acionar, 'text-orange-700 dark:text-orange-300')}
            {grupo(p, 'encerrar', 'Encerrar', m.encerrar, 'text-red-700 dark:text-red-300')}
            {grupo(p, 'conferir', 'Só conferir', m.conferir, 'opacity-80')}
            {grupo(p, 'ativ', 'Atividades', m.ativ, 'opacity-80')}
            {vazio && <p className="text-sm opacity-70">Nada pendente.</p>}
          </div>
        );
      })}
    </div>
  );

  if (embutido) return cartoes;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1.5 min-w-0">
            <CardTitle className="text-lg flex items-center gap-2 flex-wrap">
              Missões {ehHoje ? 'de hoje' : `de ${dataRef.data_br}`}
              {!aberto && visiveis.map(p => (
                <span key={p} className={`text-xs font-semibold px-2 py-0.5 rounded ${corPessoa(p)}`}>
                  {p}: {pendentes(p)} pendente{pendentes(p) === 1 ? '' : 's'}
                </span>
              ))}
            </CardTitle>
            {aberto && (
              <CardDescription>
                O que cada um aciona, encerra e confere {ehHoje ? 'hoje' : `em ${dataRef.data_br}`}, pelas listas do rodízio e pela rotina de cada status.
                Bolinha verde: feito hoje (o ✓ da verificação). Vermelha: pendente.
              </CardDescription>
            )}
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={alternar} data-testid="btn-minimizar-missoes">
            {aberto ? 'Minimizar' : 'Abrir'}
          </Button>
        </div>
      </CardHeader>
      {aberto && <CardContent>{cartoes}</CardContent>}
    </Card>
  );
};

export default MissoesHoje;
